import { apiFetch, apiUrl } from "./api";
import type { PortalRequest } from "./portal-api";

/**
 * Client for the HR-facing employee-request inbox (`/api/requests/*`).
 * Cookie-authenticated and owner-scoped: HR sees every employee's requests in
 * their workspace.
 */

/** A request as the HR inbox sees it — the portal shape plus the employee name. */
export type HrRequest = PortalRequest & { employeeName: string };

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(path), {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status})`;
    throw new Error(message);
  }
  return data as T;
}

export function fetchHrRequests(): Promise<HrRequest[]> {
  return req<HrRequest[]>("/api/requests");
}

/** Filters + paging for the HR request inbox (server-side). */
export type RequestsPageParams = {
  limit: number;
  offset: number;
  q?: string;
  status?: "pending" | "approved" | "rejected" | "cancelled";
  type?: "study_leave" | "leave" | "document" | "info_update" | "correction";
};

/** One page of requests plus the overall total for the active filters. */
export type HrRequestsPage = { items: HrRequest[]; total: number };

/** A filtered, paginated slice of the HR request inbox (`/api/requests/page`). */
export function fetchHrRequestsPage(params: RequestsPageParams): Promise<HrRequestsPage> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.q) search.set("q", params.q);
  if (params.status) search.set("status", params.status);
  if (params.type) search.set("type", params.type);
  return req<HrRequestsPage>(`/api/requests/page?${search.toString()}`);
}

export function fetchHrRequestCounts(): Promise<{
  pending: number;
  unread: number;
}> {
  return req<{ pending: number; unread: number }>("/api/requests/counts");
}

/** Mark a request read (HR opened it) — drops it from the "new" badge. */
export function markRequestRead(id: string): Promise<HrRequest> {
  return req<HrRequest>(`/api/requests/${id}/read`, { method: "POST" });
}

export function approveRequest(id: string, note?: string): Promise<HrRequest> {
  return req<HrRequest>(`/api/requests/${id}/approve`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export function rejectRequest(id: string, note?: string): Promise<HrRequest> {
  return req<HrRequest>(`/api/requests/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

/** Undo a previously-approved request (reverses any leave-balance sync). */
export function revokeRequest(id: string, note?: string): Promise<HrRequest> {
  return req<HrRequest>(`/api/requests/${id}/revoke`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}
